import { BracketMatch } from './BracketView';
import {
  addMatchListMatch,
  addMatchListPairs,
  getMatchListMatches,
  getPlayerId,
  MAX_MATCH_LIST_SIZE,
  moveMatchListMatch,
  removeMatchListMatch,
  setMatchListPlayer,
} from '../../utils/bracket';
import { formatHeight } from '../../utils/participantList';
import { useI18n } from '../../i18n';

function playerOptionLabel(t, player) {
  return [player.participantName || player.name, player.gymName ?? t('common.noGym'), formatHeight(player.height)]
    .filter(Boolean)
    .join(' · ');
}

function PlayerSelect({ value, other, participants, label, onChange }) {
  const { t } = useI18n();
  const valueId = value ? String(getPlayerId(value)) : '';
  const otherId = other ? String(getPlayerId(other)) : null;
  return (
    <select
      className="form-input match-list-select"
      aria-label={label}
      value={valueId}
      onChange={(e) =>
        onChange(participants.find((p) => String(getPlayerId(p)) === e.target.value) ?? null)
      }
    >
      <option value="">{t('bracket.matchList.selectPlayer')}</option>
      {participants.map((player) => {
        const id = String(getPlayerId(player));
        return (
          <option key={id} value={id} disabled={id === otherId}>
            {playerOptionLabel(t, player)}
          </option>
        );
      })}
    </select>
  );
}

/** 1:1 경기 편집: 선수 변경 / 순서 이동 / 삭제 / 자동 짝 추가 / 경기 추가 */
function MatchListEditor({ bracket, participants, onChange }) {
  const { t } = useI18n();
  const matches = getMatchListMatches(bracket);

  const counts = new Map();
  matches.forEach((m) =>
    [m.player1, m.player2].filter(Boolean).forEach((p) => {
      const id = String(getPlayerId(p));
      counts.set(id, (counts.get(id) ?? 0) + 1);
    })
  );
  const unpaired = participants.filter((p) => !counts.has(String(getPlayerId(p))));
  const full = matches.length >= MAX_MATCH_LIST_SIZE;

  return (
    <div className="match-list-editor">
      {matches.length === 0 && <p className="panel-empty">{t('bracket.matchList.emptyEdit')}</p>}
      <ol className="match-list-edit-rows">
        {matches.map((match, index) => {
          const no = index + 1;
          return (
            // 행 순서가 곧 경기 번호라 index 를 key 로 쓴다
            <li key={index} className="match-list-edit-row">
              <span className="match-number">{t('bracket.matchNo', { no })}</span>
              <PlayerSelect
                value={match.player1}
                other={match.player2}
                participants={participants}
                label={t('bracket.matchList.player1', { no })}
                onChange={(player) => onChange((b) => setMatchListPlayer(b, index, true, player))}
              />
              <span className="match-list-vs">vs</span>
              <PlayerSelect
                value={match.player2}
                other={match.player1}
                participants={participants}
                label={t('bracket.matchList.player2', { no })}
                onChange={(player) => onChange((b) => setMatchListPlayer(b, index, false, player))}
              />
              <div className="match-list-edit-actions">
                <button
                  type="button"
                  className="match-list-icon-btn"
                  onClick={() => onChange((b) => moveMatchListMatch(b, index, -1))}
                  disabled={index === 0}
                  aria-label={t('bracket.matchList.moveUp')}
                  title={t('bracket.matchList.moveUp')}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="match-list-icon-btn"
                  onClick={() => onChange((b) => moveMatchListMatch(b, index, 1))}
                  disabled={index === matches.length - 1}
                  aria-label={t('bracket.matchList.moveDown')}
                  title={t('bracket.matchList.moveDown')}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="match-list-icon-btn match-list-remove-btn"
                  onClick={() => onChange((b) => removeMatchListMatch(b, index))}
                  aria-label={t('bracket.matchList.remove', { no })}
                  title={t('bracket.matchList.remove', { no })}
                >
                  ×
                </button>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="match-list-edit-footer">
        <button
          type="button"
          className="btn btn-add-round"
          onClick={() => onChange((b) => addMatchListPairs(b, participants))}
          disabled={full || participants.length < 2}
          title={t('bracket.matchList.addPairsTitle')}
        >
          {t('bracket.matchList.addPairs')}
        </button>
        <button
          type="button"
          className="btn btn-add-round"
          onClick={() => onChange(addMatchListMatch)}
          disabled={full}
        >
          {t('bracket.matchList.addMatch')}
        </button>
      </div>
      {unpaired.length > 0 && (
        <p className="match-list-unpaired">
          {t('bracket.matchList.unpaired', {
            names: unpaired.map((p) => p.participantName || p.name).join(', '),
          })}
        </p>
      )}
    </div>
  );
}

/** 1:1 경기 목록 (토너먼트가 아닌 경기 여러 번). 승자 진출 없음 */
function MatchListView({
  bracket,
  participants = [],
  editMode = false,
  onChange,
  onSelectWinner,
  onEditScore,
  liveMatches,
  onStartLive,
  onEndLive,
}) {
  const { t } = useI18n();
  if (!bracket) return null;

  if (editMode && onChange) {
    return <MatchListEditor bracket={bracket} participants={participants} onChange={onChange} />;
  }

  const matches = getMatchListMatches(bracket);
  if (matches.length === 0) {
    return <p className="panel-empty">{t('bracket.matchList.empty')}</p>;
  }

  return (
    <div className="match-list">
      {matches.map((match, matchIndex) => (
        <BracketMatch
          // 경기 순서가 곧 번호라 index 를 key 로 쓴다
          key={matchIndex}
          bracket={bracket}
          match={match}
          roundIndex={0}
          matchIndex={matchIndex}
          bracketSize={bracket.bracketSize}
          editMode={false}
          onSelectWinner={onSelectWinner}
          onEditScore={onEditScore}
          liveMatches={liveMatches}
          onStartLive={onStartLive}
          onEndLive={onEndLive}
          emptyText={t('bracket.noPlayer')}
        />
      ))}
    </div>
  );
}

export default MatchListView;
